/*
 * Copyright 2026 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 */

import ai.CategorySparkline
import ai.ChartSpec
import ai.GoalProgressBar
import ai.MonteCarloFan
import ai.PercentileBand
import ai.TaxBracketOverlay

/**
 * Adapter that renders a [ChartSpec] into inline SVG for the web.
 *
 * Phase 1 ships static SVG — no React/Chart.js mount complexity, just a
 * string the personalized-card component embeds in its HTML. Phase 2a's
 * "More details" view will swap to a richer charting library (Chart.js or
 * Recharts) for interactivity, but the [ChartSpec] contract stays the same
 * so the rules engine never needs to know which renderer is downstream.
 */
object Wiz3RChart {
    /** Brand cyan from the existing theme. Keep in sync with WebTheme. */
    private const val ACCENT = "#00d9ff"
    private const val ACCENT_BAND_INNER = "rgba(0, 217, 255, 0.22)"
    private const val ACCENT_BAND_OUTER = "rgba(0, 217, 255, 0.10)"
    private const val AXIS_COLOR = "#5a5a66"
    private const val LABEL_COLOR = "#a0a0ac"

    /** Returns an `<svg>...</svg>` string ready to drop into innerHTML. */
    fun renderToSvg(spec: ChartSpec): String = when (spec) {
        is MonteCarloFan -> renderMonteCarloFan(spec)
        is GoalProgressBar -> renderGoalProgressBar(spec)
        is CategorySparkline -> renderCategorySparkline(spec)
        is TaxBracketOverlay -> renderPlaceholder(spec.title) // Phase 2 — not used yet
    }

    private fun renderMonteCarloFan(fan: MonteCarloFan): String {
        if (fan.years.isEmpty() || fan.median.isEmpty()) return renderPlaceholder(fan.title)

        // Plot area inside a 600x260 viewBox. 40px gutters for axes/labels.
        val viewW = 600
        val viewH = 260
        val plotLeft = 48
        val plotRight = viewW - 16
        val plotTop = 28
        val plotBottom = viewH - 32

        // Y range spans the widest band so the median sits inside the fan.
        val outerBand = fan.band10_90 ?: fan.band25_75
        val allValues = buildList {
            addAll(fan.median)
            outerBand?.let { addAll(it.lower); addAll(it.upper) }
            fan.band5_95?.let { addAll(it.lower); addAll(it.upper) }
        }
        val minY = allValues.min()
        val maxY = allValues.max()
        val ySpan = (maxY - minY).coerceAtLeast(1e-9)

        fun xAt(i: Int): Double {
            val frac = if (fan.years.size <= 1) 0.5 else i.toDouble() / (fan.years.size - 1)
            return plotLeft + frac * (plotRight - plotLeft)
        }
        fun yAt(v: Double): Double = plotBottom - ((v - minY) / ySpan) * (plotBottom - plotTop)

        // Build the band as a closed polygon: forward along upper, back along lower.
        fun bandPath(band: PercentileBand): String {
            val upper = band.upper.mapIndexed { i, v -> "${xAt(i)},${yAt(v)}" }
            val lower = band.lower.mapIndexed { i, v -> "${xAt(i)},${yAt(v)}" }.reversed()
            return "M " + (upper + lower).joinToString(" L ") + " Z"
        }
        val medianPath = fan.median.mapIndexed { i, v -> "${xAt(i)},${yAt(v)}" }.joinToString(" L ", prefix = "M ")

        // Three Y-axis tick lines (min, mid, max) keep the chart readable.
        val midY = (minY + maxY) / 2.0
        fun formatTick(v: Double): String = when {
            v >= 1_000_000 -> "$" + (v / 1_000_000.0).asString(1) + "M"
            v >= 1_000 -> "$" + (v / 1_000.0).asString(0) + "K"
            else -> "$" + v.asString(0)
        }

        // X-axis: first, mid, last year labels.
        val firstYear = fan.years.first()
        val lastYear = fan.years.last()
        val midIdx = fan.years.size / 2
        val midYear = fan.years[midIdx]

        val titleEsc = HtmlEscape.escape(fan.title)

        return buildString {
            append("<svg viewBox=\"0 0 $viewW $viewH\" xmlns=\"http://www.w3.org/2000/svg\" ")
            append("style=\"width:100%;height:auto;display:block;font-family:-apple-system,BlinkMacSystemFont,sans-serif;\">")

            // Title
            append("<text x=\"$plotLeft\" y=\"16\" fill=\"$LABEL_COLOR\" font-size=\"11\" font-weight=\"500\">$titleEsc</text>")

            // Bands (outer first so inner sits on top)
            fan.band10_90?.let {
                append("<path d=\"${bandPath(it)}\" fill=\"$ACCENT_BAND_OUTER\" stroke=\"none\"/>")
            }
            fan.band25_75?.let {
                append("<path d=\"${bandPath(it)}\" fill=\"$ACCENT_BAND_INNER\" stroke=\"none\"/>")
            }
            // Median line
            append("<path d=\"$medianPath\" fill=\"none\" stroke=\"$ACCENT\" stroke-width=\"1.8\" stroke-linejoin=\"round\"/>")

            // Y-axis: tick lines + labels at min/mid/max
            for ((value, label) in listOf(maxY to "max", midY to "mid", minY to "min")) {
                val y = yAt(value)
                append("<line x1=\"$plotLeft\" y1=\"$y\" x2=\"$plotRight\" y2=\"$y\" stroke=\"$AXIS_COLOR\" stroke-width=\"0.5\" stroke-dasharray=\"2,3\" opacity=\"0.4\"/>")
                append("<text x=\"${plotLeft - 6}\" y=\"${y + 3}\" fill=\"$LABEL_COLOR\" font-size=\"9\" text-anchor=\"end\">${formatTick(value)}</text>")
            }

            // X-axis labels
            append("<text x=\"${xAt(0)}\" y=\"${plotBottom + 14}\" fill=\"$LABEL_COLOR\" font-size=\"9\" text-anchor=\"start\">$firstYear</text>")
            append("<text x=\"${xAt(midIdx)}\" y=\"${plotBottom + 14}\" fill=\"$LABEL_COLOR\" font-size=\"9\" text-anchor=\"middle\">$midYear</text>")
            append("<text x=\"${xAt(fan.years.size - 1)}\" y=\"${plotBottom + 14}\" fill=\"$LABEL_COLOR\" font-size=\"9\" text-anchor=\"end\">$lastYear</text>")

            append("</svg>")
        }
    }

    private fun renderGoalProgressBar(spec: GoalProgressBar): String {
        val fraction = (spec.current / spec.target.coerceAtLeast(1e-9)).coerceIn(0.0, 1.0)
        val percent = (fraction * 100).asString(0) + "%"
        val titleEsc = HtmlEscape.escape(spec.title)
        val filledWidth = (560 * fraction).toInt()
        return """
            <svg viewBox="0 0 600 60" xmlns="http://www.w3.org/2000/svg"
                 style="width:100%;height:auto;display:block;font-family:-apple-system,BlinkMacSystemFont,sans-serif;">
                <text x="20" y="16" fill="$LABEL_COLOR" font-size="11" font-weight="500">$titleEsc</text>
                <rect x="20" y="26" width="560" height="14" rx="7" fill="rgba(255,255,255,0.06)"/>
                <rect x="20" y="26" width="$filledWidth" height="14" rx="7" fill="$ACCENT"/>
                <text x="580" y="54" fill="$LABEL_COLOR" font-size="10" text-anchor="end">$percent</text>
            </svg>
        """.trimIndent()
    }

    private fun renderCategorySparkline(spec: CategorySparkline): String {
        if (spec.values.isEmpty()) return renderPlaceholder(spec.title)
        val viewW = 600
        val viewH = 80
        val plotLeft = 8
        val plotRight = viewW - 8
        val plotTop = 20
        val plotBottom = viewH - 8
        val minV = (spec.values + listOfNotNull(spec.baselineValue)).min()
        val maxV = (spec.values + listOfNotNull(spec.baselineValue)).max()
        val vSpan = (maxV - minV).coerceAtLeast(1e-9)

        fun xAt(i: Int): Double {
            val frac = if (spec.values.size <= 1) 0.5 else i.toDouble() / (spec.values.size - 1)
            return plotLeft + frac * (plotRight - plotLeft)
        }
        fun yAt(v: Double): Double = plotBottom - ((v - minV) / vSpan) * (plotBottom - plotTop)

        val path = spec.values.mapIndexed { i, v -> "${xAt(i)},${yAt(v)}" }.joinToString(" L ", prefix = "M ")
        val baseline = spec.baselineValue?.let { yAt(it) }
        val titleEsc = HtmlEscape.escape(spec.title)

        return buildString {
            append("<svg viewBox=\"0 0 $viewW $viewH\" xmlns=\"http://www.w3.org/2000/svg\" ")
            append("style=\"width:100%;height:auto;display:block;font-family:-apple-system,BlinkMacSystemFont,sans-serif;\">")
            append("<text x=\"$plotLeft\" y=\"14\" fill=\"$LABEL_COLOR\" font-size=\"10\">$titleEsc</text>")
            if (baseline != null) {
                append("<line x1=\"$plotLeft\" y1=\"$baseline\" x2=\"$plotRight\" y2=\"$baseline\" stroke=\"$AXIS_COLOR\" stroke-width=\"0.5\" stroke-dasharray=\"3,3\"/>")
            }
            append("<path d=\"$path\" fill=\"none\" stroke=\"$ACCENT\" stroke-width=\"1.8\" stroke-linejoin=\"round\"/>")
            append("</svg>")
        }
    }

    private fun renderPlaceholder(title: String): String {
        val titleEsc = HtmlEscape.escape(title)
        return """
            <svg viewBox="0 0 600 60" xmlns="http://www.w3.org/2000/svg"
                 style="width:100%;height:auto;display:block;font-family:-apple-system,BlinkMacSystemFont,sans-serif;">
                <text x="300" y="34" fill="$LABEL_COLOR" font-size="11" text-anchor="middle" opacity="0.6">$titleEsc (chart placeholder)</text>
            </svg>
        """.trimIndent()
    }

    // Lightweight fixed-decimal formatting that doesn't pull in extra deps.
    private fun Double.asString(decimals: Int): String {
        if (decimals <= 0) return this.toLong().toString()
        val mult = generateSequence(1) { it * 10 }.elementAt(decimals).toDouble()
        val rounded = (this * mult).toLong() / mult
        // Manually format to ensure a stable decimal count
        val whole = rounded.toLong()
        val frac = ((rounded - whole) * mult).toLong().let { if (it < 0) -it else it }
        return "$whole." + frac.toString().padStart(decimals, '0')
    }
}
